import {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ChangeEvent,
} from 'react';
import { useDebouncedAutosave } from '@/hooks/useDebouncedAutosave';
import { useDocumentTitle } from '@/hooks/useDocumentTitle';
import { upsertQariDraft } from '@/lib/queries/qari';
import {
  QARI_GENDER_LABELS,
  QARI_LANGUAGE_LABELS,
  type QariGender,
  type QariLanguage,
  type QariProfile,
  type QariProfileDraft,
} from '@/types/qari';
import {
  Field,
  SelectInput,
  TextArea,
  TextInput,
  WizardShell,
  nullableText,
  renderDraftGuard,
  useQariDraftProfile,
} from '@/components/qari/wizard';
import { IconCamera, IconClose } from '@/components/home/icons';

/**
 * /qari/onboarding/identity — Step 1: Personal Identity.
 *
 * Wired to the existing Qari backend (qari_profiles via upsertQariDraft),
 * with 1-second debounced autosave. The shared wizard shell lives in
 * src/components/qari/wizard.tsx; Next here links to /qari/onboarding/expertise.
 */
export function QariOnboarding2() {
  useDocumentTitle('Qari Application · Identity');

  const draft = useQariDraftProfile();
  const guard = renderDraftGuard(draft);
  if (guard) return guard;
  // Defensive narrow — renderDraftGuard returns non-null for every phase
  // EXCEPT 'ready', so reaching this point implies phase === 'ready'.
  // TypeScript can't follow that across the helper, so we narrow explicitly.
  if (draft.phase !== 'ready') return null;
  return <Step profile={draft.profile} userId={draft.userId} />;
}

const BIO_MAX = 160;

type FormValue = Partial<QariProfileDraft>;

function Step({
  profile,
  userId,
}: {
  profile: QariProfile;
  userId: string;
}) {
  const save = useCallback(
    async (v: FormValue) => {
      await upsertQariDraft(userId, normalizePatch(v));
    },
    [userId],
  );
  const form = useDebouncedAutosave<FormValue>({
    initialValue: toFormValue(profile),
    save,
  });

  return (
    <WizardShell
      currentStep={1}
      title="Step 1: Identity & Persona"
      heading="Personal Identity"
      subtitle="Establish your public-facing profile in the Alhudah qari network. This is what students will see first."
      prev={null}
      next={{
        kind: 'link',
        label: 'Next Phase',
        to: '/qari/onboarding/expertise',
      }}
      autosave={{ status: form.status, flush: form.flush }}
    >
      <div className="grid grid-cols-1 items-start gap-8 md:grid-cols-12">
        <div className="md:col-span-4">
          <PortraitUploader />
        </div>

        <div className="space-y-6 md:col-span-8">
          <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
            <Field label="Full Name">
              <TextInput
                value={form.value.full_name ?? ''}
                onChange={(v) => form.patch({ full_name: v })}
                maxLength={120}
                placeholder="e.g. Qari Abdullah Mansour"
              />
            </Field>
            <Field label="Display Name (Optional)">
              <TextInput
                value={form.value.display_name ?? ''}
                onChange={(v) => form.patch({ display_name: v })}
                maxLength={80}
                placeholder="e.g. Al-Mansour"
              />
            </Field>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <Field label="Country" required>
              <TextInput
                value={form.value.country ?? ''}
                onChange={(v) => form.patch({ country: v })}
                maxLength={64}
                placeholder="e.g. Egypt"
              />
            </Field>
            <Field label="City" required>
              <TextInput
                value={form.value.city ?? ''}
                onChange={(v) => form.patch({ city: v })}
                maxLength={80}
                placeholder="e.g. Cairo"
              />
            </Field>
          </div>

          <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
            <Field label="Gender" required>
              <SelectInput
                value={form.value.gender ?? ''}
                onChange={(v) =>
                  form.patch({ gender: (v || null) as QariGender | null })
                }
              >
                <option value="">Select Gender</option>
                {(Object.keys(QARI_GENDER_LABELS) as QariGender[]).map((k) => (
                  <option key={k} value={k}>
                    {QARI_GENDER_LABELS[k]}
                  </option>
                ))}
              </SelectInput>
            </Field>
            <Field label="Language Fluency">
              <LanguagePicker
                value={form.value.languages ?? []}
                onChange={(v) => form.patch({ languages: v })}
              />
            </Field>
          </div>

          <Field label={`Short Bio (${BIO_MAX} characters)`}>
            <TextArea
              value={form.value.bio ?? ''}
              onChange={(v) => form.patch({ bio: v.slice(0, BIO_MAX) })}
              rows={3}
              maxLength={BIO_MAX}
              placeholder="A brief overview of your spiritual and technical journey…"
            />
            <p className="mt-1.5 text-[10px] uppercase tracking-wide text-night-300">
              {(form.value.bio ?? '').length} / {BIO_MAX}
            </p>
          </Field>
        </div>
      </div>
    </WizardShell>
  );
}

/* ---- Portrait uploader (local preview only) ---- */

function PortraitUploader() {
  const [preview, setPreview] = useState<string | null>(null);
  const inputRef = useRef<HTMLInputElement | null>(null);

  useEffect(() => {
    return () => {
      if (preview) URL.revokeObjectURL(preview);
    };
  }, [preview]);

  function handleFile(e: ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0];
    if (!file) return;
    if (preview) URL.revokeObjectURL(preview);
    setPreview(URL.createObjectURL(file));
  }

  return (
    <div className="flex flex-col items-center gap-3">
      <button
        type="button"
        onClick={() => inputRef.current?.click()}
        className="group relative flex h-40 w-40 cursor-pointer items-center justify-center overflow-hidden rounded-full border-2 border-dashed border-beam/30 bg-night-900/60 transition-colors hover:border-beam"
        aria-label="Upload portrait"
      >
        {preview ? (
          <img
            src={preview}
            alt="Selected portrait preview"
            className="absolute inset-0 h-full w-full object-cover transition-transform duration-500 group-hover:scale-105"
          />
        ) : null}
        <div className="relative z-10 flex flex-col items-center p-4 text-center">
          <IconCamera className="mb-1 h-9 w-9 text-beam" />
          <span className="text-[10px] font-semibold uppercase tracking-wide text-night-200">
            Upload Portrait (Optional)
          </span>
        </div>
        <input
          ref={inputRef}
          type="file"
          accept="image/*"
          className="sr-only"
          onChange={handleFile}
        />
      </button>
      <p className="text-[10px] uppercase tracking-wide text-night-300">
        400×400 px min
      </p>
      <p className="text-center text-[10px] leading-relaxed text-night-300/80">
        Preview only — persistence to your profile lands in the next pass.
      </p>
    </div>
  );
}

/* ---- Language picker (multi-select via chips) ---- */

function LanguagePicker({
  value,
  onChange,
}: {
  value: string[];
  onChange: (v: string[]) => void;
}) {
  const remaining = useMemo(
    () =>
      (Object.keys(QARI_LANGUAGE_LABELS) as QariLanguage[]).filter(
        (k) => !value.includes(k),
      ),
    [value],
  );

  function add(e: ChangeEvent<HTMLSelectElement>) {
    const v = e.target.value;
    if (!v) return;
    onChange([...value, v]);
    e.target.value = '';
  }

  function remove(v: string) {
    onChange(value.filter((x) => x !== v));
  }

  return (
    <div className="space-y-2">
      <SelectInput value="" onChange={() => {}} onChangeRaw={add}>
        <option value="">
          {value.length === 0 ? 'Select Language' : 'Add another…'}
        </option>
        {remaining.map((k) => (
          <option key={k} value={k}>
            {QARI_LANGUAGE_LABELS[k]}
          </option>
        ))}
      </SelectInput>
      {value.length > 0 && (
        <div className="flex flex-wrap gap-2 pt-1">
          {value.map((v) => {
            const label =
              QARI_LANGUAGE_LABELS[v as QariLanguage] ?? v.toUpperCase();
            return (
              <span
                key={v}
                className="inline-flex items-center gap-1.5 rounded-full border border-beam/30 bg-beam/10 px-3 py-1 text-xs font-semibold text-beam"
              >
                {label}
                <button
                  type="button"
                  onClick={() => remove(v)}
                  aria-label={`Remove ${label}`}
                  className="text-beam/70 transition-colors hover:text-beam"
                >
                  <IconClose className="h-3.5 w-3.5" />
                </button>
              </span>
            );
          })}
        </div>
      )}
    </div>
  );
}

/* ---- Profile <-> form bridging ---- */

function toFormValue(profile: QariProfile): FormValue {
  return {
    full_name: profile.full_name,
    display_name: profile.display_name,
    country: profile.country,
    city: profile.city,
    gender: profile.gender,
    languages: profile.languages ?? [],
    bio: profile.bio,
  };
}

function normalizePatch(v: FormValue): Partial<QariProfileDraft> {
  const out: Partial<QariProfileDraft> = {};
  if (v.full_name !== undefined) out.full_name = nullableText(v.full_name);
  if (v.display_name !== undefined)
    out.display_name = nullableText(v.display_name);
  if (v.country !== undefined) out.country = nullableText(v.country);
  if (v.city !== undefined) out.city = nullableText(v.city);
  if (v.bio !== undefined) out.bio = nullableText(v.bio);
  if (v.gender !== undefined) out.gender = v.gender;
  if (v.languages !== undefined) out.languages = v.languages ?? [];
  return out;
}
